import type { InputHTMLAttributes, ReactNode, Ref } from 'react';
import { useId } from 'react';
import { cx } from '@/lib/cx';

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  /*
   * Declared explicitly: InputHTMLAttributes doesn't include ref, and without
   * it Modal's initialFocusRef cannot target a text field — focus lands on the
   * close button instead of the first input. React 19 passes it through as an
   * ordinary prop, so spreading ...rest is enough.
   */
  ref?: Ref<HTMLInputElement>;
  /** Rendered inside the control on the leading edge; padding adjusts to suit. */
  leadingIcon?: ReactNode;
  /** Rendered inside the control on the trailing edge (e.g. a reveal toggle). */
  trailingSlot?: ReactNode;
  invalid?: boolean;
}

export function Input({ leadingIcon, trailingSlot, invalid, className, ...rest }: InputProps) {
  return (
    <div className="relative">
      {leadingIcon && (
        <span
          aria-hidden="true"
          className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-fg-subtle"
        >
          {leadingIcon}
        </span>
      )}
      <input
        aria-invalid={invalid || undefined}
        className={cx(
          'h-9 w-full rounded-md border border-border bg-surface px-2.5 text-sm text-fg max-sm:h-11',
          'placeholder:text-fg-subtle',
          'transition-colors duration-150 ease-out motion-reduce:transition-none',
          'hover:border-hairline-strong',
          // The ONE disabled rendering shared by every control (Button, Select,
          // Textarea, ColorInput, Checkbox, Radio, SegmentedControl): faded, and
          // inert to hover.
          'disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:border-border',
          'aria-invalid:border-danger',
          // Leading edge needs no breakpoint twin: the slot is a
          // pointer-events-none 16px icon at left-2.5, so 10 + 16 = 26 against
          // pl-8 = 32 reserved, at every width. Nothing there is touch-bumped.
          Boolean(leadingIcon) && 'pl-8',
          /*
           * pr-9 / pr-12 must clear the trailing slot's actual rendered width —
           * Button's sm icon size (32px / 40px touch-bumped) plus the wrapper's
           * pr-1 (4px): >=sm 36 vs 36 reserved (flush), <sm 44 vs 48 reserved.
           * A bare pr-9 below sm once overlapped the field's text — on the price
           * input's right-aligned digits, a tap toggled "no price" instead of
           * focusing the field. Hardcoded because the slot's width is decided by
           * a sibling component with no CSS length to reference; keep in sync
           * with Button's sm icon sizes and the wrapper's pr-1 below.
           */
          Boolean(trailingSlot) && 'pr-9 max-sm:pr-12',
          className,
        )}
        {...rest}
      />
      {trailingSlot && (
        <span className="absolute inset-y-0 right-0 flex items-center pr-1">{trailingSlot}</span>
      )}
    </div>
  );
}

export interface FieldProps {
  label: string;
  /** Visually hides the label while keeping it for assistive tech. */
  hideLabel?: boolean;
  hint?: ReactNode;
  error?: ReactNode;
  required?: boolean;
  /** Receives the generated id plus the describedby wiring. */
  children: (props: { id: string; 'aria-describedby': string | undefined }) => ReactNode;
}

/** Label + control + hint/error, with the id and describedby wiring done once. */
export function Field({ label, hideLabel, hint, error, required, children }: FieldProps) {
  const id = useId();
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  const describedBy = cx(Boolean(hint) && hintId, Boolean(error) && errorId) || undefined;

  /*
   * `content-start` fixes a real bug, not decoration: a Field is a grid item
   * with implicit `auto` rows, so default `align-content: stretch` hands any
   * row's surplus height (from a neighbour's hint, alert, etc.) to those
   * tracks — every OTHER field in the row grows the gap between its label and
   * control, and labels stop lining up. Pinning content to the top makes a
   * field's spacing independent of its neighbours; safe everywhere, since it
   * changes nothing when row heights already match.
   */
  return (
    <div className="grid content-start gap-1.5">
      <label
        htmlFor={id}
        className={cx(
          'text-xs font-medium text-fg-secondary',
          hideLabel && 'sr-only',
        )}
      >
        {label}
        {required && (
          <span className="ml-0.5 text-danger" aria-hidden="true">
            *
          </span>
        )}
      </label>

      {children({ id, 'aria-describedby': describedBy })}

      {hint && !error && (
        <p id={hintId} className="text-xs text-fg-subtle">
          {hint}
        </p>
      )}
      {error && (
        <p id={errorId} className="text-xs break-words text-danger">
          {error}
        </p>
      )}
    </div>
  );
}
